import moment from 'moment'
import { isAccountLevelReferenceField } from './accountReferenceDataImport'

export interface HolderReferenceDataRow {
  field: string
  value: string | null
  ord?: number | null
  rpt_date?: string | null
  upd_datetime?: string | null
}

interface HolderReferenceDataOptions {
  sourceHolder?: any
  requireDatedReference?: boolean
  holderIndex?: number
}

export interface HolderReferenceDataDifference {
  field: string
  value: string
  holderValue: string
  sourceValue: string
  holderMatchesReference: boolean
  formalChanged: boolean
}

export type ReferenceComparisonContext = 'transaction' | 'review' | 'client'

/** 取出 Holder 中可與保銀留存資料比對的欄位值。 */
export function getHolderFieldValue(holder: any, field: string): any {
  if (!holder) return undefined
  if (holder.profile && field in holder.profile) return holder.profile[field]
  if (holder.contact && field in holder.contact) return holder.contact[field]
  if (holder.bank && field in holder.bank) return holder.bank[field]
  return undefined
}

/** 將表單欄位轉成保銀留存資料可比對的文字格式。 */
export function formatReferenceFieldValue(field: any): string {
  if (typeof field === 'string' && field) return field
  if (typeof field === 'boolean') return field ? '是' : '否'
  if (field?.last_name) return [field.last_name, field.first_name].filter(Boolean).join(', ')
  if (Array.isArray(field) && field.some((f: any) => f?.phone_number !== undefined)) return formatPhoneNumbers(field)
  if (field?.street !== undefined) return [field.street, field.city, field.county, field.postal_code, field.country].filter(Boolean).join(', ')
  if (Array.isArray(field) && field.some(Boolean)) return field.filter(Boolean).join(', ')
  return ''
}

/** 比對表單欄位與保銀留存資料文字是否一致。 */
export function isReferenceFieldChanged(field: any, referenceValue?: string | null, fieldName = ''): boolean {
  if (!referenceValue) return false
  return !isReferenceFieldEqual(fieldName, field, referenceValue)
}

/** 比較表單與保銀欄位，忽略電話格式及 SWIFT 總行代碼 XXX 的表示差異。 */
export function isReferenceFieldEqual(field: string, valueA: any, valueB: any): boolean {
  return normalizeReferenceValue(field, formatReferenceFieldValue(valueA))
    === normalizeReferenceValue(field, formatReferenceFieldValue(valueB))
}

/** 比較單值或多筆電話／傳真與同欄位的全部 BNP 留存值。 */
export function areReferenceFieldValuesEqual(field: string, value: any, references: HolderReferenceDataRow[]): boolean {
  const multiplePhones = ['phone_number', 'fax_number'].includes(field)
  const values = multiplePhones && Array.isArray(value) ? value : [value]
  const normalizedValues = Array.from(new Set(values
    .map(item => normalizeReferenceValue(field, formatReferenceFieldValue(multiplePhones && typeof item === 'object' ? [item] : item)))
    .filter(Boolean)))
    .sort()
  const normalizedReferences = Array.from(new Set(references
    .map(row => normalizeReferenceValue(field, formatReferenceFieldValue(row.value)))
    .filter(Boolean)))
    .sort()
  return normalizedValues.length === normalizedReferences.length
    && normalizedValues.every((item, index) => item === normalizedReferences[index])
}

/** BNP 報表月份（沒有時用更新時間）不早於上次正式填寫時間時，判定為仍需顯示的資料。 */
export function isReferenceDataNewer(reference: HolderReferenceDataRow, formalSubmittedAt?: string | null): boolean {
  const bnpTime = parseReferenceTime(reference.rpt_date) ?? parseReferenceTime(reference.upd_datetime)
  const formalTime = parseReferenceTime(formalSubmittedAt)
  if (bnpTime === null || formalTime === null) return false

  return bnpTime >= formalTime
}

/** 初次填單顯示全部保銀資料；非初次只保留確定比正式填單新的資料。 */
export function filterRelevantReferenceData(
  referenceData: HolderReferenceDataRow[] = [],
  isInitial = false,
  formalSubmittedAt?: string | null,
): HolderReferenceDataRow[] {
  return isInitial ? referenceData : referenceData.filter(row => isReferenceDataNewer(row, formalSubmittedAt))
}

/** 依 BNP ord 取出主認購人或指定共同持有人的留存資料；舊資料沒有 ord 時保留原本的人工對照方式。 */
export function filterHolderReferenceData(referenceData: HolderReferenceDataRow[] = [], holderIndex = 0): HolderReferenceDataRow[] {
  return referenceData
    .filter(row => {
      if (isAccountLevelReferenceField(row.field)) return holderIndex === 0
      if (row.ord === null || row.ord === undefined) return true
      return row.ord === holderIndex
    })
    .sort((a, b) => Number(b.ord === holderIndex) - Number(a.ord === holderIndex))
}

/** 只保留銀行等帳戶層級的保銀留存資料。 */
export function filterAccountReferenceData(referenceData: HolderReferenceDataRow[] = []): HolderReferenceDataRow[] {
  return referenceData.filter(row => isAccountLevelReferenceField(row.field))
}

/** 找出 Holder 需確認的保銀留存資料欄位。 */
export function getHolderReferenceDataDifferences(
  holder: any,
  referenceData: HolderReferenceDataRow[] = [],
  options: HolderReferenceDataOptions = {},
): HolderReferenceDataDifference[] {
  return filterHolderReferenceData(referenceData, options.holderIndex)
    .filter(row => row.value && (!options.requireDatedReference || row.upd_datetime))
    .map(row => {
      const sourceRawValue = getHolderFieldValue(options.sourceHolder, row.field)
      const holderValue = formatReferenceFieldValue(getHolderFieldValue(holder, row.field))
      const sourceValue = formatReferenceFieldValue(sourceRawValue)
      const value = row.value || ''
      const holderMatchesReference = isReferenceFieldEqual(row.field, holderValue, value)
      return {
        field: row.field,
        value,
        holderValue,
        sourceValue,
        holderMatchesReference,
        formalChanged: sourceRawValue !== undefined && !isReferenceFieldEqual(row.field, sourceValue, value),
      }
    })
    .filter(row => {
      const holderChanged = !isReferenceFieldEqual(row.field, row.holderValue, row.value)
      if (!options.sourceHolder) return holderChanged
      return row.formalChanged && (holderChanged || row.holderMatchesReference)
    })
}

function normalizeReferenceValue(field: string, value: string): string {
  const normalized = value.trim().replace(/\s+/g, ' ').toLowerCase()
  if (field === 'phone_number' || field === 'fax_number') return normalized.replace(/\D/g, '')
  if (field === 'bank_swift_code' || field === 'intermediary_bank_swift_code') {
    return normalized.length === 11 && normalized.endsWith('xxx') ? normalized.slice(0, 8) : normalized
  }
  return normalized
}

function parseReferenceTime(value?: string | null): number | null {
  if (!value) return null
  const text = String(value).trim()
  const date = moment(text, ['YYYYMMDD', 'YYYY/MM', 'MM/DD/YYYY HH:mm:ss', moment.ISO_8601], true)
  if (/^\d{4}\/\d{2}$/.test(text)) date.endOf('month')
  return date.isValid() ? date.valueOf() : null
}

function formatPhoneNumbers(phoneNumber: Array<{ country_code?: string, area_code?: string, phone_number?: string }>): string {
  return phoneNumber.map(f => {
    if (!f.area_code && !f.country_code && !f.phone_number) return ''
    return f.area_code
      ? `+${f.country_code}-${f.area_code.replace(/\b(0+)/gi, '')}-${f.phone_number}`
      : `+${f.country_code}-${(f.phone_number || '').replace(/\b(0+)/gi, '')}`
  }).filter(Boolean).join(', ')
}
